import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Anchor, Box, Button, Container, Group, Loader, SegmentedControl, Stack, Text, Title } from '@mantine/core';
import { IconCamera, IconPhoto } from '@tabler/icons-react';
import { ScanStage, ScanStageHandle } from '../components/scan/ScanStage';
import { ScanSheet, ScanSheetHandle } from '../components/scan/ScanSheet';
import { BeerLayout, beerLines, BeerView } from '../components/scan/BeerView';
import { BeerDetails } from '../components/scan/BeerDetails';
import { FindOnMenu } from '../components/scan/FindOnMenu';
import { ScanLine } from '../components/scan/scanTypes';
import { scanErrorMessage, scanLines, scanPhoto } from '../services/scanApi';
import { preparePhoto } from '../utils/scanImage';
import classes from './MenuScan.module.css';

const SAMPLES: Record<string, string> = { 'beer-menu': 'A craft beer menu' };

interface Status { text: string; error?: boolean; busy?: boolean }

/** The photo with the beer lit; just the beer, charted or grouped by taste */
type View = 'photo' | 'beer';

/** Viewport px the floating view switch covers at the top */
const SWITCH_INSET = 52;

/**
 * Experimental: photograph a menu, and the beer on it lights up. Built on the
 * /scan-v2 page: same reading by Cloud Vision, but Jev is asked one thing of
 * each line (is it about a beer?), and nothing is translated or explained by AI.
 * A beer line that prints the name of a beer style is matched in the browser
 * against the beer guide; tapping it shows what the guide says about that style.
 */
const MenuScanBeer: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [photoMode, setPhotoMode] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [rawLines, setRawLines] = useState<ScanLine[]>([]);
  const [scanning, setScanning] = useState(false);
  const [status, setStatus] = useState<Status>({ text: '' });
  const [selected, setSelected] = useState(-1);
  const [view, setView] = useState<View>('photo');
  // Kept here so it's still the same after a look at the photo
  const [beerLayout, setBeerLayout] = useState<BeerLayout>('chart');

  const run = useRef(0);
  const pushed = useRef(false);
  const stageRef = useRef<ScanStageHandle>(null);
  const sheetOpenRef = useRef(false);
  const cameraInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const beers = useMemo(() => beerLines(rawLines), [rawLines]);
  const beerAt = useMemo(() => new Map(beers.map((b) => [b.index, b])), [beers]);
  const styled = useMemo(() => beers.filter((b) => b.items.length), [beers]);
  // On the photo: a line that names a style is marked like a known dish, the rest of the beer is plain, and everything else is greyed out
  const lines = useMemo<ScanLine[]>(() => rawLines.map((l, i) => {
    const b = beerAt.get(i);
    return { ...l, dropped: !b, hit: !!b?.items.length };
  }), [rawLines, beerAt]);
  const linesRef = useRef<ScanLine[]>([]);
  linesRef.current = lines;
  const [sheetOpen, setSheetOpen] = useState(false);
  const beerView = view === 'beer' && styled.length > 0;
  // The lines the sheet's arrows step through: the ones that name a style, and the one open now, so its arrows still work
  const order = useMemo(() => beers.filter((b) => b.items.length || b.index === selected).map((b) => b.index), [beers, selected]);
  const orderRef = useRef(order);
  orderRef.current = order;

  const sheetRef = useRef<ScanSheetHandle>(null);
  const coverTop = useCallback(() => sheetRef.current?.coverTop() ?? window.innerHeight, []);
  const closeSheet = useCallback(() => {
    sheetOpenRef.current = false;
    setSheetOpen(false);
    setSelected(-1);
    stageRef.current?.release();
  }, []);

  const enterPhotoMode = useCallback(() => {
    setPhotoMode(true);
    if (!pushed.current) {
      try { window.history.pushState({ scan: true }, ''); pushed.current = true; } catch (e) { /* ignore */ }
    }
    window.scrollTo(0, 0);
  }, []);

  const startOver = useCallback(() => {
    run.current++;
    closeSheet();
    setPhotoMode(false);
    setView('photo');
    setRawLines([]);
    setScanning(false);
    window.scrollTo(0, 0);
  }, [closeSheet]);

  useEffect(() => {
    const onPop = () => {
      if (sheetOpenRef.current) {
        closeSheet();
        try { window.history.pushState({ scan: true }, ''); } catch (e) { /* ignore */ }
        return;
      }
      pushed.current = false;
      startOver();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [closeSheet, startOver]);

  const showLines = useCallback((next: ScanLine[]) => {
    const found = beerLines(next);
    const styles = found.filter((b) => b.items.length).length;
    if (next.some((l) => l.jev !== undefined)) {
      console.groupCollapsed(`[scan-beer] Jev: ${next.filter((l) => !l.dropped).length} of ${next.length} lines are about beer, ${styles} name a style`);
      console.table(next.map((l, i) => ({
        '#': i,
        text: l.t,
        jev: l.jev == null ? 'n/a' : Number(l.jev.toFixed(2)),
        result: l.dropped ? 'DROP' : 'beer',
        style: found.find((b) => b.index === i)?.items.map((it) => it.n).join(', ') || '',
      })));
      console.groupEnd();
    }
    setRawLines(next);
    setScanning(false);
    setStatus(
      !next.length ? { text: "Couldn't find any text in this photo. Try again closer, with the menu flat and in focus.", error: true }
        : !found.length ? { text: "Couldn't find any beer on this menu." }
          : styles ? { text: 'Tap a beer to see what style it is.' }
            : { text: 'Found beer, but nothing names a style from the guide.' },
    );
  }, []);

  const beginReading = useCallback((url: string) => {
    closeSheet();
    enterPhotoMode();
    setPhotoUrl((old) => {
      if (old?.startsWith('blob:')) URL.revokeObjectURL(old);
      return url;
    });
    setView('photo');
    setRawLines([]);
    setScanning(true);
    setStatus({ text: 'Reading the menu…', busy: true });
  }, [closeSheet, enterPhotoMode]);

  const readPhoto = useCallback(async (file: File) => {
    const id = ++run.current;
    closeSheet();
    enterPhotoMode();
    setStatus({ text: 'Getting the photo ready…', busy: true });
    let prepared;
    try {
      prepared = await preparePhoto(file);
    } catch (e) {
      if (id === run.current) setStatus({ text: "That file didn't open as a photo. Try another.", error: true });
      return;
    }
    if (id !== run.current) return;
    beginReading(URL.createObjectURL(prepared.blob));
    try {
      // Nothing is looked up or saved: the styles are matched here, against the guide
      const result = await scanPhoto(prepared.blob, false, 'beer');
      if (id === run.current) showLines(result.lines);
    } catch (e) {
      if (id !== run.current) return;
      setScanning(false);
      setStatus({ text: `Couldn't read the menu: ${scanErrorMessage(e, 'something went wrong.')}`, error: true });
    }
  }, [beginReading, closeSheet, enterPhotoMode, showLines]);

  const openSample = useCallback(async (sample: string) => {
    const id = ++run.current;
    beginReading(`/scan-samples/${sample}.jpg`);
    try {
      const res = await fetch(`/scan-samples/${sample}.json`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: { engine: string; items: ScanLine[] } = await res.json();
      const result = await scanLines(data.items, data.engine, false, 'beer');
      if (id === run.current) showLines(result.lines);
    } catch (e) {
      if (id !== run.current) return;
      setScanning(false);
      setStatus({ text: `Couldn't load the sample: ${scanErrorMessage(e, (e as Error).message)}`, error: true });
    }
  }, [beginReading, showLines]);

  // ?sample=beer-menu opens a sample straight away
  const sampleParam = searchParams.get('sample');
  useEffect(() => {
    if (sampleParam && SAMPLES[sampleParam]) openSample(sampleParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleParam]);

  // Drop a late answer if the page goes away
  useEffect(() => () => { run.current++; }, []);

  /** The next line in `order`, going in `dir` from i (-1 if none). */
  const step = (i: number, dir: 1 | -1) => {
    const at = order.indexOf(i);
    return at < 0 ? -1 : order[at + dir] ?? -1;
  };

  const select = useCallback((i: number, fromList = false) => {
    if (linesRef.current[i]?.dropped) return;
    setSelected(i);
    sheetOpenRef.current = true;
    setSheetOpen(true);
    // The sheet overlays the photo: the line moves into the space above it
    stageRef.current?.reveal(i, fromList, coverTop);
  }, [coverTop]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as Element).closest('input, textarea')) return;
      if (e.key === 'Escape' && sheetOpenRef.current) closeSheet();
      if (selected < 0) return;
      const move = (dir: 1 | -1) => {
        const list = orderRef.current, at = list.indexOf(selected);
        const k = at < 0 ? -1 : list[at + dir] ?? -1;
        if (k >= 0) select(k, true);
      };
      if (e.key === 'ArrowRight') move(1);
      if (e.key === 'ArrowLeft') move(-1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [closeSheet, select, selected]);

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) readPhoto(file);
  };
  const line = selected >= 0 ? lines[selected] : null;
  const named = beerAt.get(selected)?.items || [];
  // Once the photo is read: the photo, and the beer when the menu names any styles
  const views = styled.length ? [{ value: 'photo', label: 'Photo' }, { value: 'beer', label: 'Beer' }] : [];
  const prev = selected >= 0 ? step(selected, -1) : -1;
  const next = selected >= 0 ? step(selected, 1) : -1;

  return (
    <Box className={classes.page}>
      <Container size={720} px="md">
        {!photoMode && (
          <Stack gap="lg" className={classes.intro}>
            <Stack gap="xs">
              <Text size="xs" fw={700} c="orange" tt="uppercase">Experimental</Text>
              <Title order={1} className={classes.title}>What kind of beer is this?</Title>
              <Text size="lg" c="dimmed">
                Take a photo of a beer menu. The beer on it lights up; tap a line that names a style to see how bitter, how heavy and how strong it is.
              </Text>
            </Stack>
            <Stack gap="sm">
              <Button size="lg" radius="xl" leftSection={<IconCamera size={22} />} onClick={() => cameraInput.current?.click()}>
                Photograph a menu
              </Button>
              <Group gap="lg">
                <Button variant="subtle" leftSection={<IconPhoto size={18} />} onClick={() => fileInput.current?.click()} px={0}>
                  Choose a photo
                </Button>
                {Object.entries(SAMPLES).map(([id, label]) => (
                  <Anchor key={id} component="button" type="button" size="sm" onClick={() => openSample(id)}>
                    or try {label.toLowerCase()}
                  </Anchor>
                ))}
              </Group>
              <Text size="xs" c="dimmed">
                Your photo is read by Google Cloud Vision. Beer styles: 56 styles in 7 families, each rated on 8 flavours, with
                strength, colour and what it goes with. Nothing is translated: a style is found by its name on the menu.
              </Text>
            </Stack>
          </Stack>
        )}

        {photoMode && (
          <Stack gap={0}>
            {/* Floats over the top of the photo (and stays there over the list), taking no room */}
            {views.length > 1 && (
              <div className={classes.viewSwitch}>
                <SegmentedControl
                  value={view}
                  onChange={(v) => { closeSheet(); setView(v as View); window.scrollTo(0, 0); }}
                  data={views}
                  radius="xl"
                  size="sm"
                  className={classes.viewSwitchControl}
                  aria-label="Show the menu photo, or just its beer"
                />
              </div>
            )}
            {beerView ? (
              <Box pt={SWITCH_INSET - 8}>
                <BeerView items={styled} selected={selected} onPick={(i) => select(i, true)} layout={beerLayout} onLayout={setBeerLayout} />
              </Box>
            ) : (
              <ScanStage
                ref={stageRef}
                photoUrl={photoUrl}
                lines={lines}
                selected={selected}
                scanning={scanning}
                onTapLine={(i) => select(i)}
                onTapEmpty={() => { if (sheetOpenRef.current) closeSheet(); }}
                onRetake={() => cameraInput.current?.click()}
                tall
                topInset={views.length > 1 ? SWITCH_INSET : 0}
                overlay={selected < 0 && status.text ? (
                  <Group gap={8} wrap="nowrap" role="status" aria-live="polite">
                    {status.busy && <Loader size={14} color="white" />}
                    <span style={status.error ? { fontWeight: 600, color: '#ffa8a8' } : undefined}>{status.text}</span>
                  </Group>
                ) : null}
              />
            )}
          </Stack>
        )}
      </Container>

      <ScanSheet
        ref={sheetRef}
        opened={sheetOpen}
        index={selected}
        line={line}
        position={{ index: order.indexOf(selected) + 1, total: order.length }}
        prev={prev >= 0 ? { label: lines[prev].t, onClick: () => select(prev, true) } : undefined}
        next={next >= 0 ? { label: lines[next].t, onClick: () => select(next, true) } : undefined}
        onClose={closeSheet}
        lens
      >
        {line && (
          <>
            <BeerDetails items={named} resetKey={selected} find={photoUrl && <FindOnMenu photoUrl={photoUrl} line={line} compact />} />
            {/* A line that names no style has no heading for the thumbnail to sit in */}
            {photoUrl && !named.length && <FindOnMenu photoUrl={photoUrl} line={line} />}
          </>
        )}
      </ScanSheet>

      <input ref={cameraInput} type="file" accept="image/*" capture="environment" hidden onChange={onFile} />
      <input ref={fileInput} type="file" accept="image/*" hidden onChange={onFile} />
    </Box>
  );
};

export default MenuScanBeer;
